/**
 * IntentKeywordsControlPanel — the "trap-builder" control panel for B2C Intent.
 *
 * Replaces the old passive dictionary view with:
 *  • Categories grouped by HOT / WARM / MONITOR blocks (color-coded)
 *  • Inline × delete on every chip (always visible — confirms before removing)
 *  • Bulk-import textarea per category (one term per line, parallel adds)
 *  • Per-category ON/OFF toggle (localStorage — front-only kill-switch)
 *  • Vibrant weight badges with hover tooltip explaining the score formula
 *  • Negative-keywords manager (per project — drops mentions on match)
 *  • AI suggestion (GPT) — generate 8-12 terms per category from a niche
 *  • Live Lead Preview — paste any post → see HOT/WARM/MONITOR + matched terms
 *
 * Backend dependencies:
 *  • GET    /api/b2c-intent/keyword-library
 *  • POST   /api/b2c-intent/keyword-library/add
 *  • DELETE /api/b2c-intent/keyword-library/remove
 *  • POST   /api/b2c-intent/keyword-library/ai-suggest          (NEW)
 *  • POST   /api/b2c-intent/keyword-library/classify-preview    (NEW)
 *  • GET    /api/b2c-intent/projects                            (existing)
 *  • PATCH  /api/b2c-intent/projects/{id}/negative-keywords     (NEW)
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Flame, Thermometer, Snowflake, ListPlus, Wand2,
  Eye, Power, Info, Ban, ChevronDown, ChevronUp, Sparkles, X as XIcon,
  Target,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY_DISABLED = "alp.b2c.intent.disabled_categories.v1";

// ---- Temperature buckets (drives the 3-block layout) ----------------------
const TEMP_BUCKETS = [
  {
    key: "HOT",
    icon: Flame,
    minWeight: 8,
    label_pt: "🔥 HOT — Intenção imediata",
    label_en: "🔥 HOT — Immediate intent",
    sub_pt: "Pesos 8-10. Lead pronto para abordagem agressiva.",
    sub_en: "Weights 8-10. Lead ready for aggressive outreach.",
    bg: "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)",
    bgDark: "linear-gradient(135deg, rgba(220,38,38,0.12) 0%, rgba(127,29,29,0.18) 100%)",
    border: "#EF4444",
    accent: "#DC2626",
    badgeBg: "#DC2626",
    badgeShadow: "0 0 16px rgba(220,38,38,0.45)",
  },
  {
    key: "WARM",
    icon: Thermometer,
    minWeight: 5,
    label_pt: "🌡️ WARM — Intenção emergente",
    label_en: "🌡️ WARM — Emerging intent",
    sub_pt: "Pesos 5-7. Comparação ou pedido de recomendação — nutrir.",
    sub_en: "Weights 5-7. Comparison or recommendation ask — nurture.",
    bg: "linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)",
    bgDark: "linear-gradient(135deg, rgba(217,119,6,0.12) 0%, rgba(120,53,15,0.18) 100%)",
    border: "#F59E0B",
    accent: "#B45309",
    badgeBg: "#D97706",
    badgeShadow: "0 0 16px rgba(217,119,6,0.40)",
  },
  {
    key: "MONITOR",
    icon: Snowflake,
    minWeight: 0,
    label_pt: "❄️ MONITOR — Discussão setorial",
    label_en: "❄️ MONITOR — Industry chatter",
    sub_pt: "Pesos 1-4. Pode virar lead — observar.",
    sub_en: "Weights 1-4. May convert later — observe.",
    bg: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
    bgDark: "linear-gradient(135deg, rgba(37,99,235,0.12) 0%, rgba(30,58,138,0.18) 100%)",
    border: "#3B82F6",
    accent: "#1D4ED8",
    badgeBg: "#2563EB",
    badgeShadow: "0 0 16px rgba(37,99,235,0.40)",
  },
];

// Category labels (matches backend categories).
const CAT_LABELS = {
  high_intent:           { pt: "Intenção comercial alta",       en: "High commercial intent" },
  pain_problem:          { pt: "Dor / problema",                en: "Pain / problem" },
  recommendation_request:{ pt: "Pedido de recomendação",        en: "Recommendation request" },
  complaint_competitor:  { pt: "Reclamação contra concorrente", en: "Competitor complaint" },
  comparison:            { pt: "Comparação",                    en: "Comparison" },
};

function loadDisabled() {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(STORAGE_KEY_DISABLED) || "[]"));
  } catch (_e) { return new Set(); }
}
function saveDisabled(set) {
  try { window.localStorage.setItem(STORAGE_KEY_DISABLED, JSON.stringify([...set])); } catch (_e) { /* ignore */ }
}

// ---------------------------------------------------------------------------
export default function IntentKeywordsControlPanel({ isPt }) {
  const [data, setData] = useState({ library: {}, weights: {}, classification: {}, noise_filter: [], reply_tone: "" });
  const [loading, setLoading] = useState(true);
  const [disabledCats, setDisabledCats] = useState(loadDisabled());
  const [aiOpen, setAiOpen] = useState(false);
  const [scoutOpen, setScoutOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [negativeOpen, setNegativeOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/b2c-intent/keyword-library`);
      setData(r.data || { library: {} });
    } catch (_e) {
      toast.error(isPt ? "Falha ao carregar biblioteca" : "Library load failed");
    } finally { setLoading(false); }
  }, [isPt]);
  useEffect(() => { reload(); }, [reload]);

  const toggleCategory = (cat) => {
    const next = new Set(disabledCats);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    setDisabledCats(next);
    saveDisabled(next);
    toast.success(next.has(cat)
      ? (isPt ? `"${cat}" desativada (apenas no seu painel)` : `"${cat}" disabled (your panel only)`)
      : (isPt ? `"${cat}" reativada` : `"${cat}" re-enabled`));
  };

  // Bucket categories by their weight into HOT / WARM / MONITOR.
  const bucketed = useMemo(() => {
    const out = { HOT: [], WARM: [], MONITOR: [] };
    Object.entries(data.library || {}).forEach(([cat, list]) => {
      const w = (data.weights || {})[cat] ?? 0;
      const bucket = w >= 8 ? "HOT" : w >= 5 ? "WARM" : "MONITOR";
      out[bucket].push({ cat, list: list || [], weight: w });
    });
    // Sort within bucket by weight desc.
    Object.keys(out).forEach((k) => out[k].sort((a, b) => b.weight - a.weight));
    return out;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="b2c-library">
      {/* ===== Header + global actions ===== */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h2 className="text-[22px] font-extrabold tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#8B5CF6]" />
            {isPt ? "Painel de Controle de Intenção" : "Intent Control Panel"}
          </h2>
          <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1 max-w-2xl">
            {isPt
              ? "Configure a armadilha de leads. Cada categoria pesa diferente no Intent Score; categorias HOT (peso 8-10) disparam alertas em tempo real."
              : "Set the lead trap. Each category carries a different weight; HOT (weight 8-10) categories trigger real-time alerts."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setPreviewOpen((v) => !v)}
            data-testid="intent-preview-toggle"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--al-border)] hover:bg-[var(--al-bg-soft)] text-[12px] font-bold">
            <Eye className="w-3.5 h-3.5 text-[#8B5CF6]" />
            {isPt ? "Testar lead" : "Test a lead"}
          </button>
          <button onClick={() => setAiOpen((v) => !v)}
            data-testid="intent-ai-suggest-toggle"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white text-[12px] font-bold hover:from-[#7C3AED] hover:to-[#DB2777] shadow-md">
            <Wand2 className="w-3.5 h-3.5" />
            {isPt ? "Sugerir com IA" : "AI Suggest"}
          </button>
          <button onClick={() => setScoutOpen((v) => !v)}
            data-testid="intent-niche-scout-toggle"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-gradient-to-r from-[#10B981] to-[#059669] text-white text-[12px] font-bold hover:from-[#0E9F6E] hover:to-[#047857] shadow-md">
            <Target className="w-3.5 h-3.5" />
            {isPt ? "Scout do meu pipeline" : "Scout my pipeline"}
          </button>
          <button onClick={() => setNegativeOpen((v) => !v)}
            data-testid="intent-negative-toggle"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-rose-200 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 text-[12px] font-bold">
            <Ban className="w-3.5 h-3.5" />
            {isPt ? "Palavras negativas" : "Negative keywords"}
          </button>
        </div>
      </div>

      {/* ===== Live preview (Lead tester) ===== */}
      {previewOpen && <LeadPreviewCard isPt={isPt} onClose={() => setPreviewOpen(false)} />}

      {/* ===== AI suggest ===== */}
      {aiOpen && <AISuggestCard isPt={isPt} categories={Object.keys(data.library || {})} onAdded={reload} onClose={() => setAiOpen(false)} />}

      {/* ===== AI Niche Scout (mines tenant's converted leads) ===== */}
      {scoutOpen && <NicheScoutCard isPt={isPt} onAdded={reload} onClose={() => setScoutOpen(false)} />}

      {/* ===== Negative keywords ===== */}
      {negativeOpen && <NegativeKeywordsCard isPt={isPt} onClose={() => setNegativeOpen(false)} />}

      {/* ===== Classification rules summary (HOT/WARM/MONITOR cutoffs) ===== */}
      <div className="grid sm:grid-cols-3 gap-2">
        {Object.entries(data.classification || {}).map(([code, info]) => {
          const meta = TEMP_BUCKETS.find((b) => b.key === code) || TEMP_BUCKETS[2];
          const Icon = meta.icon;
          return (
            <div key={code} data-testid={`b2c-intent-class-${code.toLowerCase()}`}
              className="rounded-xl border-2 p-3 flex items-start gap-3"
              style={{ borderColor: meta.border, background: meta.bg }}>
              <Icon className="w-5 h-5 mt-0.5" style={{ color: meta.accent }} />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[12px] font-black tracking-wider" style={{ color: meta.accent }}>[{code}]</span>
                  <span className="text-[10px] tabular-nums font-mono px-1.5 py-0.5 rounded" style={{ background: meta.badgeBg, color: "#fff" }}>≥ {info.min_score}</span>
                </div>
                <div className="text-[11.5px]" style={{ color: meta.accent }}>{isPt ? info.rule : info.rule_en}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== 3 Temperature blocks ===== */}
      {loading ? (
        <div className="flex items-center gap-2 text-[var(--al-text-muted)] text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin" /> {isPt ? "Carregando…" : "Loading…"}
        </div>
      ) : (
        <div className="space-y-5">
          {TEMP_BUCKETS.map((b) => (
            <TemperatureBlock
              key={b.key}
              bucket={b}
              entries={bucketed[b.key] || []}
              isPt={isPt}
              disabledCats={disabledCats}
              onToggleCategory={toggleCategory}
              onChange={reload}
            />
          ))}
        </div>
      )}

      {/* ===== Reply tone footer ===== */}
      {data.reply_tone && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/20 p-4 flex items-start gap-2">
          <Info className="w-4 h-4 text-emerald-700 mt-0.5" />
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1">{isPt ? "Tom da resposta sugerida" : "Suggested reply tone"}</div>
            <p className="text-[12.5px] text-[var(--al-text)] leading-relaxed">{data.reply_tone}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function TemperatureBlock({ bucket, entries, isPt, disabledCats, onToggleCategory, onChange }) {
  const Icon = bucket.icon;
  const hasContent = entries.length > 0;
  return (
    <section data-testid={`temp-block-${bucket.key.toLowerCase()}`}
      className="rounded-2xl border-2 p-5"
      style={{ borderColor: bucket.border, background: bucket.bg }}>
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: bucket.badgeBg }}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-[16px] font-extrabold tracking-tight" style={{ color: bucket.accent }}>{isPt ? bucket.label_pt : bucket.label_en}</h3>
            <p className="text-[11px]" style={{ color: bucket.accent, opacity: 0.85 }}>{isPt ? bucket.sub_pt : bucket.sub_en}</p>
          </div>
        </div>
        <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: "#fff", color: bucket.accent }}>
          {entries.length} {isPt ? "categorias" : "categories"}
        </span>
      </header>

      {!hasContent ? (
        <div className="text-[12px] italic" style={{ color: bucket.accent, opacity: 0.65 }}>
          {isPt ? "Nenhuma categoria neste bloco." : "No categories in this block."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {entries.map((e) => (
            <CategoryCard
              key={e.cat}
              cat={e.cat}
              list={e.list}
              weight={e.weight}
              bucket={bucket}
              isPt={isPt}
              disabled={disabledCats.has(e.cat)}
              onToggle={() => onToggleCategory(e.cat)}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
function CategoryCard({ cat, list, weight, bucket, isPt, disabled, onToggle, onChange }) {
  const [bulkOpen, setBulkOpen] = useState(false);
  const [singleOpen, setSingleOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [bulkDraft, setBulkDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const lbl = CAT_LABELS[cat] || { pt: cat, en: cat };
  const sorted = [...(list || [])].sort((a, b) => a.localeCompare(b));

  const submitSingle = async () => {
    const kw = draft.trim();
    if (!kw) return;
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/b2c-intent/keyword-library/add`, { category: cat, keyword: kw });
      if (res.skipped) toast.info(isPt ? "Já existe" : "Already exists");
      else toast.success(isPt ? "Adicionada" : "Added");
      setDraft("");
      setSingleOpen(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const submitBulk = async () => {
    const kws = bulkDraft.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (kws.length === 0) return;
    setBusy(true);
    try {
      const results = await Promise.allSettled(
        kws.map((kw) => axios.post(`${API}/b2c-intent/keyword-library/add`, { category: cat, keyword: kw })),
      );
      const ok = results.filter((r) => r.status === "fulfilled" && !r.value.data.skipped).length;
      const dup = results.filter((r) => r.status === "fulfilled" && r.value.data.skipped).length;
      const fail = results.filter((r) => r.status === "rejected").length;
      toast.success(isPt
        ? `${ok} adicionadas · ${dup} duplicadas · ${fail} falharam`
        : `${ok} added · ${dup} duplicates · ${fail} failed`);
      setBulkDraft("");
      setBulkOpen(false);
      onChange();
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  const remove = async (kw) => {
    if (!window.confirm(isPt ? `Remover "${kw}"?` : `Remove "${kw}"?`)) return;
    try {
      await axios.delete(`${API}/b2c-intent/keyword-library/remove`, { params: { category: cat, keyword: kw } });
      toast.success(isPt ? "Removida" : "Removed");
      onChange();
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.info(isPt ? "Palavra curada — não pode ser removida." : "Curated keyword — cannot be removed.");
      } else { toast.error(e.message); }
    }
  };

  return (
    <div data-testid={`b2c-intent-cat-${cat}`}
      className={`rounded-xl border bg-white dark:bg-slate-900/60 p-4 space-y-3 transition-all ${disabled ? "opacity-50 saturate-50" : ""}`}
      style={{ borderColor: disabled ? "#CBD5E1" : `${bucket.border}55` }}>
      <header className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="text-[13.5px] font-bold truncate">{isPt ? lbl.pt : lbl.en}</h4>
          <div className="text-[10px] uppercase tracking-wider text-[var(--al-text-muted)] mt-0.5 font-mono">{cat}</div>
        </div>
        <WeightBadge weight={weight} bucket={bucket} isPt={isPt} />
        <button onClick={onToggle} title={disabled ? (isPt ? "Reativar" : "Re-enable") : (isPt ? "Desativar" : "Disable")}
          data-testid={`b2c-intent-cat-${cat}-toggle`}
          className={`p-1.5 rounded-md border transition-colors ${disabled ? "border-slate-300 text-slate-400 hover:bg-slate-50" : "border-emerald-300 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"}`}>
          <Power className="w-3.5 h-3.5" />
        </button>
      </header>

      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {sorted.length === 0 ? (
          <span className="text-[11px] italic text-[var(--al-text-muted)]">{isPt ? "Vazia." : "Empty."}</span>
        ) : sorted.map((kw) => (
          <KeywordChip key={kw} keyword={kw} bucket={bucket} onRemove={() => remove(kw)} />
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button onClick={() => { setSingleOpen((v) => !v); setBulkOpen(false); }}
          data-testid={`b2c-intent-cat-${cat}-add-toggle`}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-[var(--al-border)] hover:bg-[var(--al-bg-soft)] text-[11px] font-semibold">
          <Plus className="w-3 h-3" /> {isPt ? "Adicionar" : "Add"}
        </button>
        <button onClick={() => { setBulkOpen((v) => !v); setSingleOpen(false); }}
          data-testid={`b2c-intent-cat-${cat}-bulk-toggle`}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-[var(--al-border)] hover:bg-[var(--al-bg-soft)] text-[11px] font-semibold">
          <ListPlus className="w-3 h-3" /> {isPt ? "Importar lista" : "Bulk import"}
        </button>
      </div>

      {singleOpen && (
        <div className="flex gap-2">
          <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitSingle(); if (e.key === "Escape") setSingleOpen(false); }}
            placeholder={isPt ? "Nova palavra-chave…" : "New keyword…"}
            data-testid={`b2c-intent-cat-${cat}-input`}
            className="flex-1 al-input h-8 text-[12px]" />
          <button onClick={submitSingle} disabled={busy || !draft.trim()}
            data-testid={`b2c-intent-cat-${cat}-submit`}
            className="h-8 px-3 rounded-md text-white text-[11px] font-bold disabled:opacity-50"
            style={{ background: bucket.badgeBg }}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (isPt ? "OK" : "OK")}
          </button>
        </div>
      )}

      {bulkOpen && (
        <div className="space-y-2">
          <textarea value={bulkDraft} onChange={(e) => setBulkDraft(e.target.value)} rows={4}
            placeholder={isPt ? "Uma por linha (ou vírgulas)…" : "One per line (or commas)…"}
            data-testid={`b2c-intent-cat-${cat}-bulk-textarea`}
            className="w-full al-input text-[12px]" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setBulkOpen(false)}
              className="h-8 px-3 rounded-md border border-[var(--al-border)] text-[11px] font-semibold">
              {isPt ? "Cancelar" : "Cancel"}
            </button>
            <button onClick={submitBulk} disabled={busy || !bulkDraft.trim()}
              data-testid={`b2c-intent-cat-${cat}-bulk-submit`}
              className="h-8 px-3 rounded-md text-white text-[11px] font-bold disabled:opacity-50 inline-flex items-center gap-1"
              style={{ background: bucket.badgeBg }}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListPlus className="w-3 h-3" />}
              {isPt ? "Importar" : "Import"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function WeightBadge({ weight, bucket, isPt }) {
  return (
    <div className="relative group">
      <span
        data-testid={`weight-badge-${weight}`}
        className="text-[11px] font-extrabold px-2.5 py-1 rounded-md text-white tabular-nums"
        style={{ background: bucket.badgeBg, boxShadow: bucket.badgeShadow }}
      >
        {isPt ? `Peso ${weight}` : `W ${weight}`}
      </span>
      <div className="absolute right-0 top-full mt-1 z-20 hidden group-hover:block pointer-events-none w-56 p-2.5 rounded-md bg-slate-900 text-white text-[10.5px] shadow-2xl">
        <div className="font-bold mb-1">{isPt ? "Como funciona o Intent Score" : "How Intent Score works"}</div>
        <div className="font-mono text-[10px] bg-white/10 px-1.5 py-1 rounded mb-1">Score = Σ (Peso × match)</div>
        <div className="text-white/85">
          {isPt
            ? `Cada match dessa categoria adiciona ${weight} ao score final. ≥70 → HOT.`
            : `Each match in this category adds ${weight} to the score. ≥70 → HOT.`}
        </div>
      </div>
    </div>
  );
}

function KeywordChip({ keyword, bucket, onRemove }) {
  return (
    <span
      className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-white dark:bg-slate-800/80 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
      style={{ borderColor: `${bucket.border}66`, color: bucket.accent }}
    >
      <span>{keyword}</span>
      <button onClick={onRemove} title="Remove"
        data-testid={`keyword-chip-remove-${keyword}`}
        className="rounded-full w-4 h-4 inline-flex items-center justify-center hover:bg-rose-100 hover:text-rose-700 transition-colors">
        <XIcon className="w-2.5 h-2.5" />
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
function LeadPreviewCard({ isPt, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/b2c-intent/keyword-library/classify-preview`, { text });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const tone = result?.classification === "HOT" ? "rose" : result?.classification === "WARM" ? "amber" : "blue";
  const toneCls = {
    rose:  "bg-rose-50 border-rose-300 text-rose-800",
    amber: "bg-amber-50 border-amber-300 text-amber-800",
    blue:  "bg-blue-50 border-blue-300 text-blue-800",
  }[tone];

  return (
    <div className="al-card p-5 border-l-4 border-l-[#8B5CF6]" data-testid="lead-preview-card">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-[#8B5CF6]" />
          <h3 className="text-[14px] font-bold">{isPt ? "Testador ao vivo" : "Live tester"}</h3>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-[var(--al-bg-soft)]"><XIcon className="w-3.5 h-3.5" /></button>
      </header>
      <p className="text-[12px] text-[var(--al-text-muted)] mb-3">
        {isPt ? "Cole um post (Reddit/fórum/Twitter) abaixo. Veremos como o classificador o pontua AGORA, com sua biblioteca atual." : "Paste any post below to see how the classifier scores it RIGHT NOW with your current library."}
      </p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder={isPt ? "Ex.: 'Estou cansado do meu plano de saúde, alguém recomenda outro?'" : "e.g.: 'I'm fed up with my health plan, anyone recommend another?'"}
        data-testid="lead-preview-input"
        className="w-full al-input text-[12.5px] mb-3" />
      <button onClick={run} disabled={busy || !text.trim()}
        data-testid="lead-preview-run"
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-[12.5px] font-bold disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        {isPt ? "Classificar" : "Classify"}
      </button>

      {result && (
        <div className={`mt-4 rounded-lg border-2 p-4 ${toneCls}`} data-testid="lead-preview-result">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-[12.5px] font-black tracking-wider">[{result.classification}]</span>
              <span className="text-[11px] tabular-nums font-mono px-2 py-0.5 rounded bg-white/70">{isPt ? "Score" : "Score"}: {result.score}</span>
            </div>
            {result.dropped && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-rose-700 text-white">{isPt ? "Descartado" : "Dropped"}</span>
            )}
          </div>
          {result.matches?.length > 0 && (
            <div className="mb-2">
              <div className="text-[10.5px] uppercase font-bold tracking-wider mb-1 opacity-75">{isPt ? "Termos encontrados" : "Matched terms"}</div>
              <div className="flex flex-wrap gap-1.5">
                {result.matches.map((m, i) => (
                  <span key={`${m.category}-${m.keyword}`} data-testid={`preview-match-${i}`}
                    className="text-[10.5px] font-mono px-1.5 py-0.5 rounded bg-white/70 border border-current">
                    {m.keyword} <span className="opacity-60">·{m.category} · +{m.weight}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {result.negative_hits?.length > 0 && (
            <div className="text-[10.5px]">
              <span className="font-bold">{isPt ? "Palavras negativas" : "Negatives"}: </span>
              {result.negative_hits.join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function AISuggestCard({ isPt, categories, onAdded, onClose }) {
  const [niche, setNiche] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState({}); // { category: [{ keyword, selected }] }

  const generate = async () => {
    if (!niche.trim()) return;
    setBusy(true);
    setSuggestions({});
    try {
      const { data } = await axios.post(`${API}/b2c-intent/keyword-library/ai-suggest`, { niche, lang: isPt ? "pt" : "en" });
      const next = {};
      Object.entries(data.suggestions || {}).forEach(([cat, list]) => {
        next[cat] = (list || []).map((kw) => ({ keyword: kw, selected: true }));
      });
      setSuggestions(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const toggle = (cat, idx) => {
    const next = { ...suggestions };
    next[cat][idx].selected = !next[cat][idx].selected;
    setSuggestions(next);
  };

  const importAll = async () => {
    const calls = [];
    Object.entries(suggestions).forEach(([cat, items]) => {
      items.filter((i) => i.selected).forEach((i) => {
        calls.push(axios.post(`${API}/b2c-intent/keyword-library/add`, { category: cat, keyword: i.keyword }));
      });
    });
    if (calls.length === 0) return;
    setBusy(true);
    try {
      const results = await Promise.allSettled(calls);
      const ok = results.filter((r) => r.status === "fulfilled" && !r.value.data.skipped).length;
      toast.success(isPt ? `${ok} adicionadas` : `${ok} added`);
      setSuggestions({});
      setNiche("");
      onAdded();
    } catch (_e) {
      toast.error(isPt ? "Falha ao importar" : "Import failed");
    } finally { setBusy(false); }
  };

  const totalSelected = Object.values(suggestions).reduce((s, list) => s + list.filter((i) => i.selected).length, 0);

  return (
    <div className="al-card p-5 border-l-4 border-l-[#EC4899]" data-testid="ai-suggest-card">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-[#EC4899]" />
          <h3 className="text-[14px] font-bold">{isPt ? "Sugestão por IA (GPT)" : "AI Suggestion (GPT)"}</h3>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-[var(--al-bg-soft)]"><XIcon className="w-3.5 h-3.5" /></button>
      </header>
      <p className="text-[12px] text-[var(--al-text-muted)] mb-3">
        {isPt ? "Digite seu nicho e a IA gera 8-12 termos para cada categoria. Marque/desmarque antes de importar." : "Enter your niche; AI generates 8-12 terms per category. Cherry-pick before importing."}
      </p>
      <div className="flex gap-2 mb-4">
        <input value={niche} onChange={(e) => setNiche(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") generate(); }}
          placeholder={isPt ? "Ex.: planos de saúde, energia solar, reformas residenciais…" : "e.g.: health insurance, solar energy, home remodeling…"}
          data-testid="ai-suggest-niche"
          className="flex-1 al-input text-[12.5px]" />
        <button onClick={generate} disabled={busy || !niche.trim()}
          data-testid="ai-suggest-run"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white text-[12.5px] font-bold disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {isPt ? "Gerar" : "Generate"}
        </button>
      </div>

      {Object.keys(suggestions).length > 0 && (
        <>
          <div className="space-y-3">
            {Object.entries(suggestions).map(([cat, items]) => {
              const lbl = CAT_LABELS[cat] || { pt: cat, en: cat };
              return (
                <div key={cat} className="rounded-lg border border-[var(--al-border)] p-3">
                  <div className="text-[11.5px] font-bold mb-2">{isPt ? lbl.pt : lbl.en} <span className="text-[var(--al-text-muted)] font-mono text-[10px]">{cat}</span></div>
                  <div className="flex flex-wrap gap-1.5">
                    {items.map((it, i) => (
                      <button key={`${cat}-${it.keyword}`} onClick={() => toggle(cat, i)}
                        data-testid={`ai-suggest-${cat}-${i}`}
                        className={`text-[11px] px-2 py-1 rounded border font-semibold transition-all ${it.selected ? "bg-[#EC4899] text-white border-[#EC4899]" : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#EC4899]/50"}`}>
                        {it.selected && "✓ "}{it.keyword}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={importAll} disabled={busy || totalSelected === 0}
              data-testid="ai-suggest-import"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-bold disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListPlus className="w-3.5 h-3.5" />}
              {isPt ? `Importar ${totalSelected} selecionadas` : `Import ${totalSelected} selected`}
            </button>
          </div>
        </>
      )}
      {/* Hint about categories used (just informational) */}
      {Object.keys(suggestions).length === 0 && categories.length > 0 && (
        <div className="text-[10.5px] text-[var(--al-text-muted)] italic">
          {isPt ? "Categorias geradas:" : "Generated for:"} {categories.join(" · ")}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function NegativeKeywordsCard({ isPt, onClose }) {
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [project, setProject] = useState(null);
  const [draft, setDraft] = useState("");
  const [bulkDraft, setBulkDraft] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/b2c-intent/projects`);
      const list = data.items || data.projects || [];
      setProjects(list);
      if (!pid && list.length > 0) setPid(list[0].id);
    } catch (_e) { /* ignore */ }
  }, [pid]);
  useEffect(() => { reload(); }, [reload]);

  const loadProject = useCallback(async () => {
    if (!pid) return;
    const found = projects.find((p) => p.id === pid);
    setProject(found || null);
  }, [pid, projects]);
  useEffect(() => { loadProject(); }, [loadProject]);

  const save = async (newList) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/b2c-intent/projects/${pid}/negative-keywords`, { negative_keywords: newList });
      toast.success(isPt ? "Salvo" : "Saved");
      // Optimistic: update local state and projects list
      setProject((p) => ({ ...(p || {}), negative_keywords: newList }));
      setProjects((prev) => prev.map((p) => p.id === pid ? { ...p, negative_keywords: newList } : p));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const negatives = project?.negative_keywords || [];

  const addOne = () => {
    const kw = draft.trim();
    if (!kw) return;
    if (negatives.map((s) => s.toLowerCase()).includes(kw.toLowerCase())) {
      toast.info(isPt ? "Já está na lista" : "Already in list");
      return;
    }
    save([...negatives, kw]);
    setDraft("");
  };
  const removeOne = (kw) => save(negatives.filter((x) => x !== kw));
  const addBulk = () => {
    const kws = bulkDraft.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (kws.length === 0) return;
    const merged = Array.from(new Set([...negatives, ...kws]));
    save(merged);
    setBulkDraft("");
    setBulkOpen(false);
  };

  return (
    <div className="al-card p-5 border-l-4 border-l-rose-500" data-testid="negative-keywords-card">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Ban className="w-4 h-4 text-rose-600" />
          <h3 className="text-[14px] font-bold">{isPt ? "Palavras Negativas (descartar lead)" : "Negative Keywords (drop lead)"}</h3>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-[var(--al-bg-soft)]"><XIcon className="w-3.5 h-3.5" /></button>
      </header>
      <p className="text-[12px] text-[var(--al-text-muted)] mb-3">
        {isPt
          ? "Posts contendo qualquer um destes termos são descartados ANTES da pontuação. Ex.: 'curso de [seu produto]', 'tutorial', 'concurso'."
          : "Posts containing any of these terms are dropped BEFORE scoring. e.g.: 'tutorial', 'course', 'free download'."}
      </p>

      {projects.length === 0 ? (
        <div className="text-[12px] italic text-[var(--al-text-muted)]">
          {isPt ? "Crie um projeto na aba Monitoramento primeiro." : "Create a project in Monitoring tab first."}
        </div>
      ) : (
        <>
          <label className="block mb-3">
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">{isPt ? "Projeto" : "Project"}</div>
            <select value={pid} onChange={(e) => setPid(e.target.value)}
              data-testid="negative-keywords-project"
              className="w-full al-input h-9 text-[12.5px]">
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>

          <div className="flex flex-wrap gap-1.5 min-h-[28px] mb-3">
            {negatives.length === 0 ? (
              <span className="text-[11px] italic text-[var(--al-text-muted)]">{isPt ? "Sem palavras negativas." : "No negatives."}</span>
            ) : negatives.map((kw) => (
              <span key={kw}
                className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border border-rose-300 bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-200">
                {kw}
                <button onClick={() => removeOne(kw)}
                  data-testid={`negative-remove-${kw}`}
                  className="rounded-full w-4 h-4 inline-flex items-center justify-center hover:bg-rose-200">
                  <XIcon className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
          </div>

          <div className="flex gap-2 mb-2">
            <input value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addOne(); }}
              placeholder={isPt ? "Adicionar termo negativo…" : "Add a negative term…"}
              data-testid="negative-keywords-input"
              className="flex-1 al-input h-8 text-[12px]" />
            <button onClick={addOne} disabled={busy || !draft.trim()}
              data-testid="negative-keywords-add"
              className="h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-bold disabled:opacity-50">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            </button>
            <button onClick={() => setBulkOpen((v) => !v)}
              className="h-8 px-2 rounded-md border border-[var(--al-border)] text-[11px] inline-flex items-center gap-1">
              <ListPlus className="w-3 h-3" />
            </button>
          </div>

          {bulkOpen && (
            <div className="space-y-2">
              <textarea value={bulkDraft} onChange={(e) => setBulkDraft(e.target.value)} rows={3}
                placeholder={isPt ? "Uma por linha…" : "One per line…"}
                data-testid="negative-bulk-textarea"
                className="w-full al-input text-[12px]" />
              <div className="flex justify-end">
                <button onClick={addBulk} disabled={busy || !bulkDraft.trim()}
                  data-testid="negative-bulk-submit"
                  className="h-8 px-3 rounded-md bg-rose-600 text-white text-[11px] font-bold disabled:opacity-50">
                  {isPt ? "Importar" : "Import"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter53 — AI Niche Scout. Reads tenant's converted leads (WON/QUALIFIED/
// PROPOSAL) and asks GPT for keywords that match the conversion profile.
function NicheScoutCard({ isPt, onAdded, onClose }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState({});

  const run = async () => {
    setBusy(true);
    setResult(null);
    setSuggestions({});
    try {
      const { data } = await axios.post(`${API}/b2c-intent/keyword-library/ai-niche-scout`,
        { lang: isPt ? "pt" : "en" });
      setResult(data);
      const next = {};
      Object.entries(data.suggestions || {}).forEach(([cat, list]) => {
        next[cat] = (list || []).map((kw) => ({ keyword: kw, selected: true }));
      });
      setSuggestions(next);
      if (data.sample_count === 0) {
        toast.info(isPt ? "Sem leads convertidos ainda. Mova alguns para WON ou QUALIFIED no Kanban." : "No converted leads yet. Move some to WON / QUALIFIED in the Kanban.");
      } else {
        toast.success(isPt
          ? `Analisei ${data.sample_count} leads convertidos.`
          : `Analyzed ${data.sample_count} converted leads.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const toggle = (cat, idx) => {
    const next = { ...suggestions };
    next[cat][idx].selected = !next[cat][idx].selected;
    setSuggestions(next);
  };

  const importAll = async () => {
    const calls = [];
    Object.entries(suggestions).forEach(([cat, items]) => {
      items.filter((i) => i.selected).forEach((i) => {
        calls.push(axios.post(`${API}/b2c-intent/keyword-library/add`, { category: cat, keyword: i.keyword }));
      });
    });
    if (calls.length === 0) return;
    setBusy(true);
    try {
      const results = await Promise.allSettled(calls);
      const ok = results.filter((r) => r.status === "fulfilled" && !r.value.data.skipped).length;
      toast.success(isPt ? `${ok} adicionadas ao seu painel` : `${ok} added to your library`);
      setSuggestions({});
      setResult(null);
      onAdded();
    } catch (_e) {
      toast.error(isPt ? "Falha ao importar" : "Import failed");
    } finally { setBusy(false); }
  };

  const totalSelected = Object.values(suggestions).reduce((s, list) => s + list.filter((i) => i.selected).length, 0);

  return (
    <div className="al-card p-5 border-l-4 border-l-[#10B981]" data-testid="niche-scout-card">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-[#10B981]" />
          <h3 className="text-[14px] font-bold">{isPt ? "Scout — analisa seu pipeline" : "Scout — analyze your pipeline"}</h3>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-[var(--al-bg-soft)]"><XIcon className="w-3.5 h-3.5" /></button>
      </header>
      <p className="text-[12px] text-[var(--al-text-muted)] mb-3">
        {isPt
          ? "A IA lê seus leads em status WON / QUALIFIED / PROPOSAL e sugere palavras-chave de intenção que combinam com o perfil dos clientes que JÁ fecharam com você."
          : "AI reads your WON / QUALIFIED / PROPOSAL leads and suggests intent keywords matching the profile of clients who ALREADY converted with you."}
      </p>
      <button onClick={run} disabled={busy}
        data-testid="niche-scout-run"
        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-gradient-to-r from-[#10B981] to-[#059669] text-white text-[12.5px] font-bold disabled:opacity-50 mb-4">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        {isPt ? "Analisar pipeline com IA" : "Run pipeline analysis"}
      </button>

      {result && result.sample_count > 0 && (
        <>
          <div className="grid sm:grid-cols-2 gap-3 mb-4 text-[11px]">
            <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 p-2 border border-emerald-200/50">
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-emerald-700 mb-1">{isPt ? "Setores dominantes" : "Top sectors"}</div>
              {result.top_sectors.map((s) => (
                <div key={s.sector} className="flex justify-between"><span className="capitalize">{s.sector}</span><span className="font-bold tabular-nums">{s.count}</span></div>
              ))}
            </div>
            <div className="rounded-md bg-blue-50 dark:bg-blue-950/30 p-2 border border-blue-200/50">
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-blue-700 mb-1">{isPt ? "Cidades dominantes" : "Top cities"}</div>
              {result.top_cities.slice(0, 6).map((c) => (
                <div key={c.city} className="flex justify-between"><span className="capitalize">{c.city}</span><span className="font-bold tabular-nums">{c.count}</span></div>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            {Object.entries(suggestions).map(([cat, items]) => {
              const lbl = CAT_LABELS[cat] || { pt: cat, en: cat };
              if (items.length === 0) return null;
              return (
                <div key={cat} className="rounded-lg border border-[var(--al-border)] p-3">
                  <div className="text-[11.5px] font-bold mb-2">{isPt ? lbl.pt : lbl.en} <span className="text-[var(--al-text-muted)] font-mono text-[10px]">{cat}</span></div>
                  <div className="flex flex-wrap gap-1.5">
                    {items.map((it, i) => (
                      <button key={`${cat}-${it.keyword}`} onClick={() => toggle(cat, i)}
                        data-testid={`niche-scout-${cat}-${i}`}
                        className={`text-[11px] px-2 py-1 rounded border font-semibold transition-all ${it.selected ? "bg-[#10B981] text-white border-[#10B981]" : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#10B981]/50"}`}>
                        {it.selected && "✓ "}{it.keyword}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={importAll} disabled={busy || totalSelected === 0}
              data-testid="niche-scout-import"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-bold disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListPlus className="w-3.5 h-3.5" />}
              {isPt ? `Importar ${totalSelected} selecionadas` : `Import ${totalSelected} selected`}
            </button>
          </div>
        </>
      )}
      {result && result.sample_count === 0 && (
        <div className="text-[12px] text-[var(--al-text-muted)] italic p-3 rounded bg-amber-50 border border-amber-200">
          {result.note}
        </div>
      )}
    </div>
  );
}

